import { Skeleton, Stack } from '@mui/joy';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useAuthUser } from 'auth/hooks';
import { Confirmation } from 'core/components';
import { CacheGroup, CacheImpact } from 'dashboard/enums';
import { useClearCache, useGetCaches } from 'dashboard/hooks';
import { ICacheEntry } from 'dashboard/types';
import { Color, Size, SkeletonVariant } from 'theme/enums';

import { CacheGroupCard } from './CacheGroupCard';
import { CacheLoadError } from './CacheLoadError';

// Ordered by widening blast radius: this environment, then every environment on this
// web server, then every environment in the deployment. Within the widest tier the
// external integrations come last, being the most expensive to rebuild.
const GROUP_ORDER = [CacheGroup.FILE, CacheGroup.APCU, CacheGroup.REDIS, CacheGroup.INTEGRATION];

const SKELETON_COUNT = 3;

export const ClearCaches = () => {
  const { t } = useTranslation();

  const [pendingCache, setPendingCache] = useState<ICacheEntry | null>(null);

  const { user } = useAuthUser();

  // Mirrors the page's admin guard so the request never leaves the browser for a
  // non-admin, even though the endpoint refuses them anyway.
  const { caches, environment, isError, isLoading, refetchCaches } = useGetCaches({
    enabled: !!user?.admin,
  });

  const { clearCache, isClearCacheLoading } = useClearCache();

  const [clearingKey, setClearingKey] = useState<string | null>(null);

  // Always confirm. Clearing is not reversible, and the dialog is where the
  // consequence is spelled out — a bare "Clear" button says nothing about whether
  // this costs a database rebuild or a round of external API calls.
  const onClear = useCallback((cache: ICacheEntry) => setPendingCache(cache), []);

  const onConfirmClear = useCallback(async () => {
    if (!pendingCache) {
      return;
    }

    const { key } = pendingCache;

    setPendingCache(null);
    setClearingKey(key);

    try {
      await clearCache({ cache: key });
    } catch {
      // Surfaced as a toast by useHandleErrors' onError; swallowed to keep an
      // HTTP-level failure off window.onunhandledrejection.
    } finally {
      setClearingKey(null);
    }
  }, [clearCache, pendingCache]);

  if (isError) {
    return <CacheLoadError onRetry={refetchCaches} />;
  }

  if (isLoading) {
    return (
      <Stack spacing={2}>
        {Array.from({ length: SKELETON_COUNT }, (_, index) => (
          <Skeleton
            key={index}
            sx={{ borderRadius: Size.SM, height: 160, position: 'relative' }}
            variant={SkeletonVariant.RECTANGULAR}
          />
        ))}
      </Stack>
    );
  }

  const pendingLabel = pendingCache ? t(`labelCache_${pendingCache.key}`, pendingCache.key) : '';

  return (
    // PageContentWrapper is the scroll container and has no bottom padding, so without
    // this the last card ends flush against the viewport and the page reads as cut off.
    <Stack spacing={2} sx={{ pb: 3 }}>
      {GROUP_ORDER.map((group) => (
        <CacheGroupCard
          caches={caches.filter((cache) => group === cache.group)}
          clearingKey={isClearCacheLoading ? clearingKey : null}
          environment={environment}
          group={group}
          key={group}
          onClear={onClear}
        />
      ))}

      <Confirmation
        confirmBtnColor={Color.DANGER}
        confirmBtnLabel={t('buttonLabelClear')}
        isOpen={null !== pendingCache}
        message={t(
          CacheImpact.HIGH === pendingCache?.impact
            ? 'messageClearCacheConfirmHighImpact'
            : 'messageClearCacheConfirm',
          { cache: pendingLabel }
        )}
        onCancel={() => setPendingCache(null)}
        onConfirm={onConfirmClear}
        title={t('titleClearCacheConfirm')}
      />
    </Stack>
  );
};
